// One resolver for every place that turns a kernel command or canvas reference
// into executable definition bytes.
//
// The resolver owns two invariants:
//   1. command lookup has one rank: bundled → viewer-owned → tenant-visible;
//   2. canvas UUID lookup is exact and never falls through to a command.
//
// Definition source lives outside the body. A body cannot mint trust by adding
// a `definitionSource` field to itself; callers place bytes into one of the
// resolver's source-aware buckets instead.

export const DEFINITION_SOURCES = Object.freeze({
    BUNDLED: "bundled",
    LIVE: "live",
    STORED: "stored",
    UNKNOWN: "unknown",
});

// Definition and invocation sources deliberately have the same wire values,
// but separate names keep the two halves of the trust decision visible at
// call sites.
export const INVOCATION_SOURCES = Object.freeze({
    BUNDLED: "bundled",
    LIVE: "live",
    STORED: "stored",
    UNKNOWN: "unknown",
});

export const RUN_SURFACES = Object.freeze({
    CANVAS: "canvas",
    CHAT: "chat",
    EDITOR: "editor",
    BACKGROUND: "background",
    UNKNOWN: "unknown",
});

export const KERNEL_RESOLUTION_TIERS = Object.freeze({
    BUNDLED: "bundled",
    VIEWER_OWNED: "viewer-owned",
    LEGACY_VIEWER: "legacy-viewer",
    TENANT_VISIBLE: "tenant-visible",
});

const SOURCE_VALUES = new Set(Object.values(DEFINITION_SOURCES));
const RUN_SURFACE_VALUES = new Set(Object.values(RUN_SURFACES));
const TRUSTED_SOURCE_VALUES = new Set([
    DEFINITION_SOURCES.BUNDLED,
    DEFINITION_SOURCES.LIVE,
]);
const RESOLVED_KERNEL_ENVELOPES = new WeakSet();
const RUN_CONTEXT_ENVELOPES = new WeakSet();
const SNAPSHOT_SOURCE_BODIES = new WeakMap();

// Storage uses this association to recover tenant identity without trusting
// fields in definition bytes. Execution still uses the immutable snapshot.
export function kernelSnapshotSourceBody(body) {
    return SNAPSHOT_SOURCE_BODIES.get(body) || body;
}

function normalizedSource(source) {
    return SOURCE_VALUES.has(source) ? source : DEFINITION_SOURCES.UNKNOWN;
}

// Source authority belongs to the exact definition data that the resolver
// observed. Keep a private immutable snapshot so mutating the caller-owned
// object after resolution cannot change executable bytes under an existing
// LIVE/BUNDLED envelope.
function snapshotDefinitionBody(value, seen = new Map()) {
    if (value === null || value === undefined) return value;
    const type = typeof value;
    if (type !== "object") {
        if (type === "function" || type === "symbol") {
            throw new TypeError("kernel definitions must contain data, not functions or symbols");
        }
        return value;
    }
    if (seen.has(value)) return seen.get(value);

    const isArray = Array.isArray(value);
    const prototype = Object.getPrototypeOf(value);
    if (!isArray && prototype !== Object.prototype && prototype !== null) {
        throw new TypeError("kernel definitions must contain only plain objects and arrays");
    }

    const snapshot = isArray ? [] : {};
    seen.set(value, snapshot);
    for (const key of Object.keys(value)) {
        Object.defineProperty(snapshot, key, {
            value: snapshotDefinitionBody(value[key], seen),
            enumerable: true,
            configurable: false,
            writable: false,
        });
    }
    return Object.freeze(snapshot);
}

function resolvedKernel(body, definitionSource = DEFINITION_SOURCES.UNKNOWN) {
    const snapshot = snapshotDefinitionBody(body);
    if (snapshot && typeof snapshot === "object") {
        SNAPSHOT_SOURCE_BODIES.set(snapshot, kernelSnapshotSourceBody(body));
    }
    const envelope = Object.freeze({
        body: snapshot,
        definitionSource: normalizedSource(definitionSource),
    });
    RESOLVED_KERNEL_ENVELOPES.add(envelope);
    return envelope;
}

// Use when propagation receives a bare body. Missing provenance must remove
// authority, never infer it from fields controlled by those bytes.
export function asResolvedKernel(value) {
    if (value && typeof value === "object"
        && RESOLVED_KERNEL_ENVELOPES.has(value)) {
        return value;
    }
    // A structural lookalike can arrive from a server payload or a clone that
    // dropped provenance. Keep its bytes, but never accept its source claim.
    if (value && typeof value === "object"
        && Object.prototype.hasOwnProperty.call(value, "body")) {
        return resolvedKernel(value.body, DEFINITION_SOURCES.UNKNOWN);
    }
    return resolvedKernel(value, DEFINITION_SOURCES.UNKNOWN);
}

// Preserve provenance while rebuilding or otherwise transforming definition
// bytes. Passing a bare body as `value` intentionally produces UNKNOWN.
export function mapResolvedKernel(value, transform) {
    const current = asResolvedKernel(value);
    const body = typeof transform === "function"
        ? transform(current.body)
        : transform;
    return resolvedKernel(body, current.definitionSource);
}

function makeRunContext(
    invocationSource = INVOCATION_SOURCES.UNKNOWN,
    metadata = {},
) {
    const context = Object.freeze({
        ...(metadata && typeof metadata === "object" ? metadata : {}),
        invocationSource: normalizedSource(invocationSource),
        surface: RUN_SURFACE_VALUES.has(metadata?.surface)
            ? metadata.surface
            : RUN_SURFACES.UNKNOWN,
    });
    RUN_CONTEXT_ENVELOPES.add(context);
    return context;
}

// Source-specific entry points make authority assignment explicit in review.
// There is intentionally no public constructor that accepts an arbitrary
// source value.
export function createBundledRunContext(metadata = {}) {
    return makeRunContext(INVOCATION_SOURCES.BUNDLED, metadata);
}

export function createLiveRunContext(metadata = {}) {
    return makeRunContext(INVOCATION_SOURCES.LIVE, metadata);
}

export function createStoredRunContext(metadata = {}) {
    return makeRunContext(INVOCATION_SOURCES.STORED, metadata);
}

export function createUnknownRunContext(metadata = {}) {
    return makeRunContext(INVOCATION_SOURCES.UNKNOWN, metadata);
}

// Keep interaction metadata (consent broker, explicit agent-run click, etc.)
// while making a missing or invalid invocation source fail closed.
export function asRunContext(value) {
    if (!value || typeof value !== "object"
        || !RUN_CONTEXT_ENVELOPES.has(value)) {
        return makeRunContext(INVOCATION_SOURCES.UNKNOWN);
    }
    return makeRunContext(value.invocationSource, value);
}

// Explicit metadata transformations (for example attaching a consent broker)
// use this helper so provenance survives. Ordinary clone/spread does not.
export function mapRunContext(value, metadata = {}) {
    const current = asRunContext(value);
    return makeRunContext(current.invocationSource, {
        ...current,
        ...(metadata && typeof metadata === "object" ? metadata : {}),
        // Surface affects realm routing; only a source-specific constructor
        // may assign it. Consent/click transformations cannot move a canvas
        // run into the chat realm.
        surface: current.surface,
    });
}

// Join sources at a model/composition boundary. UNKNOWN wins because it means
// propagation was lost; otherwise STORED taints the result. Mixing LIVE with
// BUNDLED remains LIVE because the result was created in this interaction.
export function joinKernelSources(...sources) {
    if (sources.length === 0) return DEFINITION_SOURCES.UNKNOWN;
    const normalized = sources.map(normalizedSource);
    if (normalized.includes(DEFINITION_SOURCES.UNKNOWN)) {
        return DEFINITION_SOURCES.UNKNOWN;
    }
    if (normalized.includes(DEFINITION_SOURCES.STORED)) {
        return DEFINITION_SOURCES.STORED;
    }
    if (normalized.includes(DEFINITION_SOURCES.LIVE)) {
        return DEFINITION_SOURCES.LIVE;
    }
    return DEFINITION_SOURCES.BUNDLED;
}

export function taintRunContext(value, ...contextSources) {
    const current = asRunContext(value);
    return makeRunContext(
        joinKernelSources(current.invocationSource, ...contextSources),
        current,
    );
}

// Model output can be LIVE but can never become BUNDLED. Stored or unknown
// model context propagates unchanged into the emitted definition envelope.
export function resolvedKernelFromModel(body, value) {
    const { invocationSource } = asRunContext(value);
    return resolvedKernel(
        body,
        invocationSource === INVOCATION_SOURCES.BUNDLED
            ? DEFINITION_SOURCES.LIVE
            : invocationSource,
    );
}

export function evaluateKernelRunTrust(definition, context) {
    const resolvedDefinition = asResolvedKernel(definition);
    const runContext = asRunContext(context);
    const definitionSource = resolvedDefinition.definitionSource;
    const invocationSource = runContext.invocationSource;
    return {
        resolvedDefinition,
        runContext,
        definitionSource,
        invocationSource,
        trusted: TRUSTED_SOURCE_VALUES.has(definitionSource)
            && TRUSTED_SOURCE_VALUES.has(invocationSource),
    };
}

export function normalizeKernelCommand(raw) {
    if (!raw) return "";
    return String(raw)
        .trim()
        .replace(/^\//, "")
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
}

function definitionBody(row) {
    if (!row || typeof row !== "object") return null;
    if (Object.prototype.hasOwnProperty.call(row, "body")) return row.body;
    if (Object.prototype.hasOwnProperty.call(row, "definition")) return row.definition;
    return row;
}

function readField(row, body, camel, snake = camel) {
    if (row && Object.prototype.hasOwnProperty.call(row, camel)) return row[camel];
    if (snake !== camel && row && Object.prototype.hasOwnProperty.call(row, snake)) return row[snake];
    const metadata = row?.metadata;
    if (metadata && typeof metadata === "object"
        && Object.prototype.hasOwnProperty.call(metadata, camel)) {
        return metadata[camel];
    }
    if (snake !== camel && metadata && typeof metadata === "object"
        && Object.prototype.hasOwnProperty.call(metadata, snake)) {
        return metadata[snake];
    }
    if (body && Object.prototype.hasOwnProperty.call(body, camel)) return body[camel];
    if (snake !== camel && body && Object.prototype.hasOwnProperty.call(body, snake)) return body[snake];
    return undefined;
}

const CANDIDATE_METADATA_FIELDS = [
    ["kind", "kind"],
    ["title", "title"],
    ["description", "description"],
    ["visibility", "visibility"],
    ["tenantId", "tenant_id"],
    ["createdBy", "created_by"],
    ["createdByLabel", "created_by_label"],
    ["authorLabel", "author_label"],
    ["objectOrigin", "object_origin"],
    ["sourceArtifactId", "source_artifact_id"],
    ["hiddenAt", "hidden_at"],
    ["withdrawnAt", "withdrawn_at"],
    ["forkedFrom", "forked_from"],
    ["formatVersion", "format_version"],
    ["createdAt", "created_at"],
    ["updatedAt", "updated_at"],
    ["version", "version"],
];

function candidateMetadata(row, body, id, command) {
    const nested = row?.metadata;
    const metadata = nested && typeof nested === "object"
        ? { ...nested }
        : {};
    // Metadata accompanies a resolution for display. It can never carry a
    // source claim that looks like the resolver-owned execution envelope.
    delete metadata.definitionSource;
    delete metadata.definition_source;
    delete metadata.invocationSource;
    delete metadata.invocation_source;
    delete metadata.source;
    delete metadata.trusted;
    delete metadata.isTrusted;
    delete metadata.body;
    delete metadata.definition;

    for (const [camel, snake] of CANDIDATE_METADATA_FIELDS) {
        const value = readField(row, body, camel, snake);
        if (value !== undefined) metadata[camel] = value;
    }
    if (id) metadata.id = id;
    if (command) metadata.command = command;
    return Object.freeze(metadata);
}

function commandOf(row, body) {
    return normalizeKernelCommand(readField(row, body, "command"));
}

function idOf(row, body) {
    const value = readField(row, body, "id");
    return typeof value === "string" && value ? value : null;
}

function isKernelRow(row, body) {
    const kind = readField(row, body, "kind");
    return kind == null || kind === "kernel";
}

function isRetracted(row, body) {
    return readField(row, body, "hiddenAt", "hidden_at") != null
        || readField(row, body, "withdrawnAt", "withdrawn_at") != null;
}

function isTenantVisible(row, body) {
    if (isRetracted(row, body)) return false;
    return readField(row, body, "visibility") === "published";
}

function makeEntry(row, definitionSource, tier, visible) {
    const body = definitionBody(row);
    if (!body || typeof body !== "object" || !isKernelRow(row, body)) return null;
    if (!visible(row, body)) return null;
    const command = commandOf(row, body);
    const id = idOf(row, body);
    return {
        command,
        id,
        tier,
        metadata: candidateMetadata(row, body, id, command),
        resolved: resolvedKernel(body, definitionSource),
    };
}

function collect(rows, definitionSource, tier, visible) {
    const out = [];
    for (const row of rows || []) {
        const entry = makeEntry(row, definitionSource, tier, visible);
        if (entry) out.push(entry);
    }
    return out;
}

const AVAILABLE = (row, body) => !isRetracted(row, body);
const ALWAYS = () => true;

export function createKernelResolver({
    bundledKernels = [],
    viewerOwnedKernels = [],
    visibleTenantKernels = [],
    // Explicit compatibility bucket for pre-cloud, localStorage definitions.
    // They rank as viewer-owned but carry UNKNOWN source because no source
    // envelope accompanied the persisted body.
    legacyViewerKernels = [],
} = {}) {
    const ranked = [
        ...collect(
            bundledKernels,
            DEFINITION_SOURCES.BUNDLED,
            KERNEL_RESOLUTION_TIERS.BUNDLED,
            ALWAYS,
        ),
        ...collect(
            viewerOwnedKernels,
            DEFINITION_SOURCES.STORED,
            KERNEL_RESOLUTION_TIERS.VIEWER_OWNED,
            AVAILABLE,
        ),
        ...collect(
            legacyViewerKernels,
            DEFINITION_SOURCES.UNKNOWN,
            KERNEL_RESOLUTION_TIERS.LEGACY_VIEWER,
            AVAILABLE,
        ),
        ...collect(
            visibleTenantKernels,
            DEFINITION_SOURCES.STORED,
            KERNEL_RESOLUTION_TIERS.TENANT_VISIBLE,
            isTenantVisible,
        ),
    ];

    const resolveByCommand = (rawCommand) => {
        const command = normalizeKernelCommand(rawCommand);
        if (!command) return null;
        return ranked.find((entry) => entry.command === command)?.resolved || null;
    };

    const resolveById = (id) => {
        if (typeof id !== "string" || !id) return null;
        return ranked.find((entry) => entry.id === id)?.resolved || null;
    };

    const listByCommand = () => {
        const seen = new Set();
        const result = [];
        for (const entry of ranked) {
            if (!entry.command || seen.has(entry.command)) continue;
            seen.add(entry.command);
            result.push(entry.resolved);
        }
        return result;
    };

    const asCandidate = (entry) => Object.freeze({
        id: entry.id,
        command: entry.command,
        tier: entry.tier,
        metadata: entry.metadata,
        resolvedDefinition: entry.resolved,
    });

    // Unlike listByCommand(), this intentionally retains shadows. It powers
    // author-disambiguation UI while resolveByCommand() remains the one winner.
    const listCandidates = () => Object.freeze(
        ranked
            .filter((entry) => entry.command)
            .map(asCandidate),
    );

    const candidatesByCommand = (rawCommand) => {
        const command = normalizeKernelCommand(rawCommand);
        if (!command) return Object.freeze([]);
        return Object.freeze(
            ranked
                .filter((entry) => entry.command === command)
                .map(asCandidate),
        );
    };

    return Object.freeze({
        resolveByCommand,
        resolveById,
        listByCommand,
        listCandidates,
        candidatesByCommand,
    });
}

// Adapter for a legacy local list whose trusted bundled bodies are supplied
// separately by identity. Never inspect `body.builtin`: bytes loaded from
// storage could set that field and must not be able to mint BUNDLED source.
// Cloud rows belong in viewerOwnedKernels or visibleTenantKernels instead.
export function createLegacyLocalKernelResolver(
    kernels = [],
    { bundledKernels = [] } = {},
) {
    const bundledBodies = new Set(bundledKernels);
    return createKernelResolver({
        bundledKernels: kernels.filter((kernel) => bundledBodies.has(kernel)),
        legacyViewerKernels: kernels.filter((kernel) => !bundledBodies.has(kernel)),
    });
}

export function canvasCellKernelId(cell) {
    if (!cell || typeof cell !== "object") return null;
    const value = cell.kernel_id ?? cell.kernelId ?? cell.kernel_ref_id ?? cell.kernelRefId;
    return typeof value === "string" && value ? value : null;
}

// Command-only canvas cells are a localStorage compatibility shape. Callers
// must opt into this adapter; resolveCanvasCellKernel never falls back by
// command on its own.
export function legacyCommandCanvasCellAdapter(cell) {
    if (!cell || typeof cell !== "object" || canvasCellKernelId(cell)) return null;
    const command = normalizeKernelCommand(cell.command);
    return command ? { command } : null;
}

export function resolveCanvasCellKernel(
    cell,
    resolver,
    { legacyAdapter = null } = {},
) {
    if (!resolver) return null;
    const kernelId = canvasCellKernelId(cell);
    if (kernelId) {
        // Load-bearing: an invalid/missing UUID is unavailable. A command on
        // the same cell must never redirect it to different bytes.
        return resolver.resolveById(kernelId);
    }
    if (typeof legacyAdapter !== "function") return null;
    const legacyRef = legacyAdapter(cell);
    const command = typeof legacyRef === "string" ? legacyRef : legacyRef?.command;
    return command ? resolver.resolveByCommand(command) : null;
}

const SERVER_CANVAS_FIELDS = [
    "visibility",
    "version",
    "createdBy",
    "created_by",
    "objectOrigin",
    "object_origin",
    "tenantId",
    "tenant_id",
    "canEdit",
    "can_edit",
    "canDelete",
    "can_delete",
    "canFork",
    "can_fork",
    "canPublish",
    "can_publish",
    "hiddenAt",
    "hidden_at",
    "withdrawnAt",
    "withdrawn_at",
    "sourceArtifactId",
    "source_artifact_id",
    "forkedFrom",
    "forked_from",
];

// This predicate only chooses whether the old localStorage adapter is allowed.
// Bundled canvases have manifest UUIDs; server-shaped canvases must be strict.
export function isLegacyLocalCanvas(canvas) {
    if (!canvas || typeof canvas !== "object" || canvas.builtin === true) return false;
    return !SERVER_CANVAS_FIELDS.some((key) => Object.prototype.hasOwnProperty.call(canvas, key));
}
